/**
 * MapControlsMenu Component - Floating menu for map controls
 * Contains all map control buttons in a collapsible menu
 */
import React, { useState } from 'react';
import './MapControlsMenu.css';

interface MapControlsMenuProps {
  addPoleMode: boolean;
  showONUs: boolean;
  showVirtualPoles: boolean;
  showSignal: boolean;
  addCableByClickMode: boolean;
  deleteCableMode: boolean;
  selectedCablesCount: number;
  onToggleAddPoleMode: () => void;
  onToggleShowONUs: () => void;
  onToggleShowVirtualPoles: () => void;
  onToggleShowSignal: () => void;
  onToggleAddCableByClickMode: () => void;
  onToggleDeleteCableMode: () => void;
  onDeleteSelectedCables: () => void;
  onRecalculateColors: () => void;
  onAddCable: () => void;
  onImportPoles: () => void;
  onExportPoles: () => void;
  onShowStatistics: () => void;
}

export const MapControlsMenu: React.FC<MapControlsMenuProps> = ({
  addPoleMode,
  showONUs,
  showVirtualPoles,
  showSignal,
  addCableByClickMode,
  deleteCableMode,
  selectedCablesCount,
  onToggleAddPoleMode,
  onToggleShowONUs,
  onToggleShowVirtualPoles,
  onToggleShowSignal,
  onToggleAddCableByClickMode,
  onToggleDeleteCableMode,
  onDeleteSelectedCables,
  onRecalculateColors,
  onAddCable,
  onImportPoles,
  onExportPoles,
  onShowStatistics,
}) => {
  const [isOpen, setIsOpen] = useState(false); // Menu is closed by default

  console.log('[MapControlsMenu] Component rendered', {
    addPoleMode,
    showONUs,
    showVirtualPoles,
    isOpen
  });

  const handleToggle = () => {
    console.log('[MapControlsMenu] Toggle clicked, isOpen:', !isOpen);
    setIsOpen(!isOpen);
  };

  const handleAction = (action: () => void) => {
    action();
    // Keep menu open for toggle actions
  };

  return (
    <>
      {isOpen && (
        <div 
          className="map-controls-overlay" 
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
      
      <div className={`map-controls-menu ${isOpen ? 'open' : ''}`}>
        {/* Menu items */}
        {isOpen && (
          <div className="map-controls-items">
            <button
              className="map-controls-item"
              onClick={() => {
                handleAction(onToggleAddPoleMode);
                // Aj tento režim sa ovláda klikaním do mapy — pri ZAPNUTÍ menu
                // zavrieme, nech sa má technik kam trafiť. Pri vypínaní nie.
                if (!addPoleMode) setIsOpen(false);
              }}
              style={{
                backgroundColor: addPoleMode ? '#10b981' : '#6b7280',
                color: 'white'
              }}
              title={addPoleMode ? "Vypnúť režim pridávania stĺpikov" : "Zapnúť režim pridávania stĺpikov"}
            >
              <span className="map-controls-icon">{addPoleMode ? '✅' : '📍'}</span>
              <span className="map-controls-label">
                {addPoleMode ? 'Pridávanie stĺpikov ZAPNUTÉ' : 'Pridávanie stĺpikov VYPNUTÉ'}
              </span>
            </button>

            <button
              className="map-controls-item"
              onClick={() => handleAction(onToggleShowONUs)}
              style={{
                backgroundColor: showONUs ? '#10b981' : '#6b7280',
                color: 'white'
              }}
              title={showONUs ? "Skryť všetky ONU" : "Zobraziť všetky ONU"}
            >
              <span className="map-controls-icon">👁️</span>
              <span className="map-controls-label">
                {showONUs ? 'Skryť ONU' : 'Zobraziť ONU'}
              </span>
            </button>

            <button
              className="map-controls-item"
              onClick={() => handleAction(onToggleShowSignal)}
              style={{
                backgroundColor: showSignal ? '#10b981' : '#6b7280',
                color: 'white',
              }}
              title={showSignal
                ? 'Skryť farebný prstenec so stavom signálu'
                : 'Zobraziť, kde je signál pripravený na zváranie'}
            >
              <span className="map-controls-icon">📶</span>
              <span className="map-controls-label">
                {showSignal ? 'Skryť signál' : 'Zobraziť signál'}
              </span>
            </button>

            <button
              className="map-controls-item"
              onClick={() => handleAction(onToggleShowVirtualPoles)}
              style={{
                backgroundColor: showVirtualPoles ? '#10b981' : '#6b7280',
                color: 'white'
              }}
              title={showVirtualPoles ? "Skryť zákaznícke kazety" : "Zobraziť zákaznícke kazety"}
            >
              <span className="map-controls-icon">👁️</span>
              <span className="map-controls-label">
                {showVirtualPoles ? 'Skryť zákaznícke kazety' : 'Zobraziť zákaznícke kazety'}
              </span>
            </button>

            <button
              className="map-controls-item"
              onClick={() => {
                handleAction(onRecalculateColors);
                setIsOpen(false);
              }}
              style={{ backgroundColor: '#8b5cf6', color: 'white' }}
              title="Prepočítať farby splitterov"
            >
              <span className="map-controls-icon">🎨</span>
              <span className="map-controls-label">Prepočítať farby</span>
            </button>

            <button
              className="map-controls-item"
              onClick={() => {
                handleAction(onAddCable);
                setIsOpen(false);
              }}
              title="Pridať kábel"
            >
              <span className="map-controls-icon">🔌</span>
              <span className="map-controls-label">Pridať kábel</span>
            </button>

            <button
              className={`map-controls-item ${addCableByClickMode ? 'active' : ''}`}
              onClick={() => {
                handleAction(onToggleAddCableByClickMode);
                // Tento režim sa ovláda klikaním do mapy, takže otvorené menu
                // by prekážalo — pri ZAPNUTÍ ho zavrieme. Pri vypínaní nie,
                // nech sa dá plynulo pokračovať ďalšou voľbou z menu.
                if (!addCableByClickMode) setIsOpen(false);
              }}
              style={addCableByClickMode ? { backgroundColor: '#8b5cf6', color: 'white' } : {}}
              title="Pridávanie kábla ťahom"
            >
              <span className="map-controls-icon">🔗</span>
              <span className="map-controls-label">Pridávanie kábla ťahom</span>
            </button>

            <button
              className={`map-controls-item ${deleteCableMode ? 'active' : ''}`}
              onClick={() => {
                handleAction(onToggleDeleteCableMode);
              }}
              style={deleteCableMode ? { backgroundColor: '#ef4444', color: 'white' } : {}}
              title="Vymazať káble - klikni na káble ktoré chceš vymazať"
            >
              <span className="map-controls-icon">🗑️</span>
              <span className="map-controls-label">
                {deleteCableMode ? `Vymazať káble (${selectedCablesCount})` : 'Vymazať káble'}
              </span>
            </button>

            {deleteCableMode && selectedCablesCount > 0 && (
              <button
                className="map-controls-item"
                onClick={() => {
                  handleAction(onDeleteSelectedCables);
                  setIsOpen(false);
                }}
                style={{ backgroundColor: '#dc2626', color: 'white' }}
                title={`Vymazať ${selectedCablesCount} označených káblov`}
              >
                <span className="map-controls-icon">❌</span>
                <span className="map-controls-label">Vymazať označené ({selectedCablesCount})</span>
              </button>
            )}

            <button
              className="map-controls-item"
              onClick={() => {
                handleAction(onImportPoles);
                setIsOpen(false);
              }}
              title="Import stĺpikov zo súboru"
            >
              <span className="map-controls-icon">📥</span>
              <span className="map-controls-label">Import stĺpikov</span>
            </button>

            <button
              className="map-controls-item"
              onClick={() => {
                handleAction(onExportPoles);
                setIsOpen(false);
              }}
              title="Export stĺpov do CSV alebo KML"
            >
              <span className="map-controls-icon">📤</span>
              <span className="map-controls-label">Export stĺpov</span>
            </button>

            <button
              className="map-controls-item"
              onClick={() => {
                handleAction(onShowStatistics);
                setIsOpen(false);
              }}
              title="Štatistiky využitia vlákien"
            >
              <span className="map-controls-icon">📊</span>
              <span className="map-controls-label">Štatistiky vlákien</span>
            </button>
          </div>
        )}

        {/* Main toggle button */}
        <button
          className="map-controls-toggle"
          onClick={handleToggle}
          aria-label={isOpen ? 'Zavrieť menu' : 'Otvoriť menu'}
          aria-expanded={isOpen}
          title="Ovládanie mapy"
        >
          <span className={`map-controls-toggle-icon ${isOpen ? 'rotate' : ''}`}>
            {isOpen ? '✕' : '⚙️'}
          </span>
        </button>
      </div>
    </>
  );
};

